/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Component imports
import { Button, Card, Checkbox, Col, Collapse, Form, Input, Row, Select, Space, Tooltip, Typography } from 'antd';
import {
  DeleteOutlined,
  DownOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons';
import LabelWithOriginalText from '../../../Forms/Fields/LabelWithOriginalText';
import RichTextEditor from '../../../Forms/Fields/RichTextEditor/RichTextEditor';
import CSATSectionsDropdown from '../../../UI/CSATSectionsDropdown';
import { CSATStarIcon } from '../../../Icons/CSATStarIcon';
import { CSATNumberIcon } from '../../../Icons/CSATNumberIcon';
import { CSATEmojiesIcon } from '../../../Icons/CSATEmojiesIcon';

// Misc imports
import { useBotLanguage } from '../../../../utils/hooks/useBotLanguage';
import { CSAT_NODE_RATING_STYLES } from '../../../../constants/flowGraph';
import { getCurrentQueryState } from '../../../../store/utils/getCurrentQueryState';
import { useParams, useSearchParams } from 'react-router-dom';
import { ACTIVITY_TYPES, VARIABLE_TYPES } from '../../../../constants/main';
import { ALPHANUMERIC_HYPHEN_UNDERSCORE_REGEX } from '../../../../constants/regex';

// Constant declarations
const { Text, Paragraph } = Typography;

const CsatNodeForm = ({ form }) => {
  const { t } = useTranslation();
  const { isEditingPrimaryLanguage, botLanguages, currentBotLanguage, primaryBotLanguage } = useBotLanguage();
  const { orgId, tenantId } = useParams();
  const [searchParams] = useSearchParams();

  const [selectedSectionIds, setSelectedSectionIds] = useState([]);
  const { data: activity } = getCurrentQueryState('fetchActivity', { orgId, tenantId, activityId: searchParams.get('activity'), currentBotLanguage }, !searchParams.get('activity'));

  const sections = Form.useWatch(['data', 'sections'], form) || [];
  const openEndedQuestionEnabled = Form.useWatch(
    ['data', 'openEndedQuestionEnabled'],
    form
  );

  useEffect(() => {
    const initialSections = form.getFieldValue(['data', 'sections']) || [];
    const initialSelectedIds = initialSections
      .map(section => section?.id)
      .filter(id => !!id);

    // Update state with initial selected IDs
    setSelectedSectionIds(initialSelectedIds);
  }, [form]);

  const renderNumbersOption = () => (
    <Fragment>
      <Row>
        <Col xs={4}>
          <Text>
            {t('Numbers')}
          </Text>
        </Col>
        <Col xs={20} className="d-flex justify-content-start">
          <Typography.Text className="fs-sm d-flex justify-content-center align-items-center">
            {
              Array.from({ length: 5 }, (_, i) => i + 1).map((index) => (
                <CSATNumberIcon
                  className="ml-1"
                  key={index}
                  index={index}
                />
              ))
            }
          </Typography.Text>
        </Col>
      </Row>
    </Fragment>
  );

  const renderStarsOption = () => (
    <Fragment>
      <Row>
        <Col xs={4}>
          <Text>
            {t('Stars')}
          </Text>
        </Col>
        <Col xs={20} className="d-flex justify-content-start">
          <Typography.Text className="d-flex justify-content-center align-items-center">
            {
              Array.from({ length: 5 }, (_, i) => i + 1).map((index) => (
                <CSATStarIcon key={index} />
              ))
            }
          </Typography.Text>
        </Col>
      </Row>
    </Fragment>
  );

  const renderEmotionsOption = () => (
    <Fragment>
      <Row>
        <Col xs={4}>
          <Text>
            {t('Emotions')}
          </Text>
        </Col>
        <Col xs={20} className="d-flex justify-content-start">
          <Typography.Text className="d-flex justify-content-center align-items-center">
            {
              Array.from({ length: 3 }, (_, i) => i + 1).map((index) => (
                <CSATEmojiesIcon key={index} index={index} className="ml-1" />
              ))
            }
          </Typography.Text>
        </Col>
      </Row>

    </Fragment>
  );

  return (
    <Fragment>
      <Row gutter={16} className="mb-4">
        <Col xs={24}>
          <LabelWithOriginalText label={t('Question')} originalText={form.getFieldValue(['data', 'label', primaryBotLanguage])} />
          {
            botLanguages.map(lang => (
              <Form.Item
                label=""
                name={['data', 'label', lang]}
                hidden={currentBotLanguage !== lang}
                rules={[{ required: lang === primaryBotLanguage && isEditingPrimaryLanguage, message: t('Question cannot be blank.') }]}
              >
                <RichTextEditor
                  placeholder={t('Write some content')}
                  includedVariableTypes={activity?.type === ACTIVITY_TYPES.LIVECHAT_FLOW ? [VARIABLE_TYPES.LIVECHAT] : []}
                />
              </Form.Item>
            ))
          }
        </Col>

        <Col xs={24}>
          <Form.Item
            label={t('Rating Type & Scale')}
            name={['data', 'ratingStyle']}
            rules={[
              {
                required: true,
                message: t('Rating Type & Scale cannot be blank.')
              }
            ]}
          >
            <Select
              size="large"
              placeholder={t('Select the rating type')}
              options={[
                { label: renderNumbersOption(), value: CSAT_NODE_RATING_STYLES.NUMBERS },
                { label: renderStarsOption(), value: CSAT_NODE_RATING_STYLES.STARS },
                { label: renderEmotionsOption(), value: CSAT_NODE_RATING_STYLES.EMOTIONS }
              ]}
              getPopupContainer={triggerNode => triggerNode.parentElement}
            />
          </Form.Item>
        </Col>

        <Col xs={24}>
          <Paragraph>
            <Space>
              {t('Add up to 3 separate rating Sections')}
              <Tooltip
                placement="right"
                title={
                  <Fragment>
                    <Paragraph>
                      {t('In webchat deployment, you can add up to 3 separate rating sections (eg. Responsiveness, Knowledge Satisfaction) in addition to the main feedback question.')}
                    </Paragraph>
                    <Paragraph className="mb-0">
                      {t('Once you add at least one rating section, the rating scale will appear under the sections instead of the main feedback question.')}
                    </Paragraph>
                  </Fragment>
                }
              >
                <InfoCircleOutlined />
              </Tooltip>
            </Space>
          </Paragraph>
        </Col>

        <Col xs={24}>
          <Form.List
            name={['data', 'sections']}
          >
            {(fields, { add, remove }, { errors }) => (
              <Fragment>
                <Row className="mb-3">
                  <Col xs={24}>
                    {fields.length === 0 ? add() : null}
                    {
                      fields.map(({ key, name, ...restField }, _index) => (
                        <Card
                          size="small"
                          className="bg-layout border-radius-base shadow-none mb-3 position-relative w-90"
                          key={key}
                        >
                          <Row gutter={[16, 16]}>
                            <Col xs={24}>
                              <Form.Item
                                hidden
                                name={[name, 'id']}
                                {...restField}
                              >
                                <Input />
                              </Form.Item>
                              <Form.Item
                                {...restField}
                                name={[name, 'title']}
                                className="mb-0 hide-error"
                                label={t('Rating Section')}
                                requiredMark
                                rules={[{ required: true, message: 'Please select a section category' }]}
                              >
                                <CSATSectionsDropdown
                                  onChange={(value) => {
                                    form.setFieldValue([name, 'title'], value);
                                  }}
                                  onSelectCSATSectionSetId={(sectionId) => {
                                    form.setFieldValue(['data', 'sections', name, 'id'], sectionId);
                                    setSelectedSectionIds(sections.map(section => section?.id));
                                  }}
                                  placeholder={t('Enter the section category')}
                                  size="large"
                                  disabled={!isEditingPrimaryLanguage}
                                  selectedSectionIds={selectedSectionIds}
                                />
                              </Form.Item>
                            </Col>

                            <Col xs={24}>
                              <LabelWithOriginalText
                                label={
                                  <Space size={4}>
                                    <Text>{t('Title')}</Text>
                                    <Text type="secondary">{t('(optional)')}</Text>
                                  </Space>
                                }
                                originalText={form.getFieldValue(['data', 'sections', name, 'label', primaryBotLanguage])}
                              />
                              {
                                botLanguages.map(lang => (
                                  <Form.Item
                                    {...restField}
                                    name={[name, 'label', lang]}
                                    className="mb-2 hide-error"
                                    label=""
                                    hidden={currentBotLanguage !== lang}
                                  >
                                    <Input
                                      onChange={(e) => {
                                        form.setFieldValue([name, 'label', lang], e.target.value);
                                      }}
                                      placeholder={t('Enter the section title')}
                                      size="large"
                                    />
                                  </Form.Item>
                                ))
                              }

                            </Col>
                          </Row>
                          <Button
                            icon={<DeleteOutlined />}
                            size="large"
                            type="text"
                            className="position-absolute top-0"
                            style={{ right: '-10%' }}
                            onClick={() => {
                              const currentSection = sections[name];

                              if (currentSection && currentSection.id) {
                                setSelectedSectionIds(prev => prev.filter(id => id !== currentSection.id));
                              }

                              remove(name);
                            }}
                            disabled={!isEditingPrimaryLanguage || (fields.length === 1)}
                          />
                        </Card>

                      ))
                    }
                  </Col>
                </Row>

                <Row gutter={16} hidden={fields?.length >= 3}>
                  <Col sm={12} xs={24}>
                    <Form.Item>
                      <Button
                        type="dashed"
                        size="large"
                        block
                        onClick={() => add()}
                        icon={<PlusOutlined />}
                        disabled={!isEditingPrimaryLanguage || (openEndedQuestionEnabled && sections.length >= 2)}
                      >
                        {t('Add Section')}
                      </Button>
                    </Form.Item>
                  </Col>
                </Row>

                <Form.ErrorList errors={errors} />
              </Fragment>
            )}
          </Form.List>
        </Col>
        <Col xs={24}>
          <Form.Item
            name={['data', 'openEndedQuestionEnabled']}
            valuePropName="checked"
            className="mb-0"
          >
            <Checkbox disabled={sections.length >= 3}>
              <Space>
                {t('Enable open-ended question')}
                {
                  sections.length >= 3
                    ? (<Tooltip
                      title={t('Open-ended question is only available with maximum two rating sections enabled.')}
                      trigger="hover"
                    >
                      <InfoCircleOutlined />
                    </Tooltip>)
                    : null
                }
              </Space>
            </Checkbox>
          </Form.Item>
        </Col>
        <Col xs={24} hidden={!openEndedQuestionEnabled}>
          <LabelWithOriginalText label={t('Open-ended question')} originalText={form.getFieldValue(['data', 'openEndedQuestionText', primaryBotLanguage])} />
          {
            botLanguages.map(lang => (
              <Form.Item
                label=""
                name={['data', 'openEndedQuestionText', lang]}
                hidden={currentBotLanguage !== lang}
                rules={[{ required: openEndedQuestionEnabled && lang === primaryBotLanguage && isEditingPrimaryLanguage, message: t('Open-ended question text cannot be blank.') }]}
              >
                <Input
                  placeholder={t('Enter the question')}
                  size="large"
                />
              </Form.Item>
            ))
          }
        </Col>
      </Row>

      <Row gutter={16} className="mb-1">
        <Col xs={24}>
          <Collapse
            bordered={false}
            expandIcon={({ isActive }) => <DownOutlined rotate={isActive ? 180 : 0} />}
          >
            <Collapse.Panel
              header={t('Advanced Settings')}
              forceRender
            >
              <Form.Item
                name={['data', 'variableName']}
                label={<Text>
                  {t('Variable name')}
                  <Tooltip
                    className="ml-1"
                    placement="top"
                    title={t('It is used for storing the answer to this question. Set a variable name to use the answer in messages templates, or in control flow nodes.')}
                  >
                    <QuestionCircleOutlined className="fs-sm position-absolute" />
                  </Tooltip>
                </Text>}
              >
                <Input
                  placeholder={t('Enter the variable name')}
                  size="large"
                  onKeyPress={(e) => {
                    if (!ALPHANUMERIC_HYPHEN_UNDERSCORE_REGEX.test(e.key)) {
                      e.preventDefault();
                      return false;
                    }
                  }}
                  maxLength={30}
                  disabled={!isEditingPrimaryLanguage}
                />
              </Form.Item>

              <LabelWithOriginalText label={t('Submit button label')} originalText={form.getFieldValue(['data', 'submitLabel', primaryBotLanguage])} />
              {
                botLanguages.map(lang => (
                  <Form.Item
                    label=""
                    name={['data', 'submitLabel', lang]}
                    hidden={currentBotLanguage !== lang}
                  >
                    <Input
                      placeholder={t('Enter a submit label')}
                      size="large"
                    />
                  </Form.Item>
                ))
              }
            </Collapse.Panel>
          </Collapse>
        </Col>
      </Row>
    </Fragment>
  );
};

CsatNodeForm.propTypes = {
  form: PropTypes.object,
  setCanSubmit: PropTypes.func.isRequired
};

export default CsatNodeForm;
